import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { SkillSession } from "@/components/coping/SkillSession";
import { Textarea } from "@/components/ui/textarea";
import { logError } from "@/lib/log";

export const PlanSkill = ({ best, chosenId, onNext }) => {
  const [skill, setSkill] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get("/coping-skills").then(({ data }) => {
      const chosen = chosenId && data.find((s) => s.id === chosenId);
      const mine = data.find((s) => s.id === best?.skill_id) || data.find((s) => s.name === best?.name);
      setSkill(chosen ? { ...chosen, chosen: true } : mine ? { ...mine, mine: true } : data.find((s) => s.category === "anxiety") || data[0] || null);
    }).catch((e) => logError("Plan skills failed:", e));
  }, [best, chosenId]);
  return (
    <div data-testid="plan-step-skill">
      <p className="text-sm text-mm-muted">{skill?.chosen ? "The skill you chose for tough days." : skill?.mine ? `Lately, this has lowered your stress the most (−${best.avg_drop} on average).` : "A gentle skill to start your day."}</p>
      {skill && (
        <div className="mm-soft p-4 mt-4 flex items-center gap-3">
          <span className="text-3xl">{skill.icon}</span>
          <div className="flex-1 min-w-0"><div className="font-semibold" data-testid="plan-skill-name">{skill.name}</div><div className="text-xs text-mm-muted truncate">{skill.description}</div></div>
          <button className="mm-btn !py-2 !px-4 text-sm shrink-0" onClick={() => setOpen(true)} data-testid="plan-skill-start">Start</button>
        </div>
      )}
      <button className="mm-btn-ghost w-full mt-5" onClick={onNext} data-testid="plan-skill-next">Next: 1-minute breathing</button>
      {open && skill && <SkillSession skill={skill} onClose={() => { setOpen(false); onNext(); }} />}
    </div>
  );
};

const PHASES = [["Breathe in", 4000, 1.35], ["Breathe out", 6000, 0.8]];

export const PlanBreathe = ({ onNext }) => {
  const [left, setLeft] = useState(null);
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (left === null || left <= 0) return undefined;
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [left]);
  const running = left !== null && left > 0;
  // Depends on `running` (not `left`) so the 1s countdown doesn't reset the phase timer
  useEffect(() => {
    if (!running) return undefined;
    const t = setTimeout(() => setPhase((p) => 1 - p), PHASES[phase][1]);
    return () => clearTimeout(t);
  }, [phase, running]);
  const [label, ms, scale] = PHASES[phase];
  return (
    <div className="text-center" data-testid="plan-step-breathe">
      <div className="h-56 flex items-center justify-center">
        <div className="w-32 h-32 rounded-full mm-gradient flex items-center justify-center text-white font-semibold"
          style={{ transform: `scale(${running ? scale : 1})`, transition: `transform ${ms}ms ease-in-out` }} data-testid="plan-breathe-circle">
          {running ? label : left === 0 ? "Well done" : "Ready"}
        </div>
      </div>
      {running && <div className="text-sm text-mm-muted" data-testid="plan-breathe-timer">{left}s left</div>}
      {left === null && <button className="mm-btn w-full mt-2" onClick={() => { setPhase(0); setLeft(60); }} data-testid="plan-breathe-start">Start 1 minute</button>}
      <button className="mm-btn-ghost w-full mt-3" onClick={onNext} data-testid="plan-breathe-next">{left === 0 ? "Next" : "Skip for now"}</button>
    </div>
  );
};

const PRESET = "Today's a tough day for me. I don't need anything fixed — just wanted you to know. 💜";

export const PlanSupporter = ({ plan, onNext }) => {
  const [sid, setSid] = useState(plan.supporters[0]?.id);
  const [msg, setMsg] = useState(PRESET);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(plan.told_supporter);
  const [prompted, setPrompted] = useState(plan.prompted);
  const tellAll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tough-day-plan/tell-supporters"); toast.success(data.message); setPrompted(true); }
    catch (e) { toast.error(errMsg(e, "Could not send")); } finally { setBusy(false); }
  };
  const tellAllBtn = plan.prompt_supporters > 0 && (prompted
    ? <p className="mm-soft p-3 mt-3 text-sm" data-testid="plan-tell-supporters-done">Your supporters know a kind note today could help 💜</p>
    : <button className="mm-btn-ghost w-full mt-3" disabled={busy} onClick={tellAll} data-testid="plan-tell-supporters">Tell my supporters ({plan.prompt_supporters})</button>);
  const send = async () => {
    setBusy(true);
    try { const { data } = await api.post("/tough-day-plan/tell-supporter", { supporter_id: sid, message: msg }); toast.success(data.message); setSent(true); }
    catch (e) { toast.error(errMsg(e, "Could not send")); } finally { setBusy(false); }
  };
  if (!plan.supporters.length) return (
    <div data-testid="plan-step-supporter">
      <p className="text-sm text-mm-muted">Think of one person you trust. You can add a supporter any time to share how you're doing.</p>
      <Link to="/supporters" className="mm-btn-ghost w-full mt-4 inline-flex justify-center" data-testid="plan-add-supporter">Add a supporter</Link>
      <button className="mm-btn w-full mt-3" onClick={onNext} data-testid="plan-supporter-next">Next</button>
    </div>
  );
  return (
    <div data-testid="plan-step-supporter">
      <p className="text-sm text-mm-muted">You don't have to carry today alone. Optional — nothing is sent unless you tap send.</p>
      <div className="flex flex-wrap gap-1.5 mt-4">
        {plan.supporters.map((s) => <button key={s.id} className={`mm-chip !py-1.5 ${sid === s.id ? "mm-chip-on" : ""}`} onClick={() => setSid(s.id)} data-testid={`plan-supporter-${s.id}`}>{s.name}</button>)}
      </div>
      {sent ? <p className="mm-soft p-3 mt-4 text-sm" data-testid="plan-supporter-sent">Sent. They know today is a tough day 💜</p> : (
        <>
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 300))} rows={3} className="mt-3" data-testid="plan-supporter-message" />
          <button className="mm-btn w-full mt-3 inline-flex items-center justify-center gap-2" disabled={busy || !msg.trim()} onClick={send} data-testid="plan-supporter-send">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Let them know today's a tough day
          </button>
        </>
      )}
      {tellAllBtn}
      <button className="mm-btn-ghost w-full mt-3" onClick={onNext} data-testid="plan-supporter-next">{sent ? "Next" : "Skip"}</button>
    </div>
  );
};
